import { createRole, deleteRole, deleteRoles, fetchRoles, updateRole } from 'api';
import useAxios from 'hooks/useAxios';
import { useUser } from 'contexts/userContext';
import { checkIfError } from 'utils/helpers';
import {
  IBulkDeleteRequest,
  IRoleResourceWithUsers,
  IRoleResource,
  IRoleResourceWithRelations,
} from '../../server/types';

const useCreateRole = () => {
  const [request, { loading, error }] = useAxios<IRoleResource>();

  const handleCreateRole = async (data: Omit<IRoleResource, 'id'>) => {
    const response = await request(createRole(data));

    return response;
  };

  return [handleCreateRole, { loading, error: error?.data?.message }] as const;
};

const useUpdateRole = () => {
  const [user, setUser] = useUser();
  const [request, { loading, error }] = useAxios<IRoleResource>();

  const handleUpdateRole = async (id: string, data: IRoleResource) => {
    const response = await request(updateRole(id, data));

    if (checkIfError(response)) return response;

    if (user?.role?._id === id) {
      setUser((prevUser) => ({
        ...prevUser!,
        role: response as IRoleResourceWithRelations,
      }));
    }

    return response;
  };

  return [handleUpdateRole, { loading, error: error?.data?.message }] as const;
};

const useFetchRoles = () => {
  const [request, { loading, error }] = useAxios<IRoleResourceWithUsers[]>();

  const handleFetchRoles = async () => {
    const users = await request(fetchRoles());

    return users;
  };

  return [handleFetchRoles, { loading, error: error?.data?.message }] as const;
};

const useDeleteRole = () => {
  const [request, { loading, error }] = useAxios<IRoleResource>();

  const handleDeleteRole = async (id: string) => {
    const role = await request(deleteRole(id));

    return role;
  };

  return [handleDeleteRole, { loading, error: error?.data?.message }] as const;
};

const useBulkDeleteRoles = () => {
  const [request, { loading, error }] = useAxios<IRoleResource[]>();

  const handleBulkDeleteRoles = async (data: IBulkDeleteRequest) => {
    const users = await request(deleteRoles(data));

    return users;
  };

  return [handleBulkDeleteRoles, { loading, error: error?.data?.message }] as const;
};

export { useCreateRole, useUpdateRole, useFetchRoles, useDeleteRole, useBulkDeleteRoles };
